import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router";
import { api, ApiError } from "@/api";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@osmix/ui/card";
import { Input } from "@osmix/ui/input";
import { Label } from "@osmix/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { useAuth } from "@/store";
type LocationState = {
  from?: {
    pathname?: string;
  };
} | null;
export function LoginPage() {
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const status = useAuth(s => s.status);
  const signIn = useAuth(s_0 => s_0.signIn);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Already signed in — skip the form. Honour where the guard sent us from.
  if (status === "authenticated") {
    const from = (location.state as LocationState)?.from?.pathname ?? "/";
    return <Navigate to={from} replace />;
  }
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const session = await api.auth.login({
        email,
        password
      });
      signIn(session.token, session.user);
      const from_0 = (location.state as LocationState)?.from?.pathname ?? "/";
      void navigate(from_0, {
        replace: true
      });
    } catch (err) {
      // A 401 means the credentials were rejected; anything else (network
      // failure, 5xx, unreachable gateway) is a connection problem — don't
      // mislabel it as a bad password.
      const rejected = err instanceof ApiError && err.status === 401;
      setError(t(rejected ? "auth.error" : "auth.errorConnection"));
      setSubmitting(false);
    }
  };
  return <div className="flex min-h-screen items-center justify-center bg-background p-4">
			<Card className="w-full max-w-sm">
				<CardHeader>
					<CardTitle className="text-xl">{t("auth.title")}</CardTitle>
					<CardDescription>{t("auth.subtitle")}</CardDescription>
				</CardHeader>
				<CardContent>
					<form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
						<div className="flex flex-col gap-2">
							<Label htmlFor="email">{t("auth.email")}</Label>
							<Input id="email" type="email" autoComplete="username" autoFocus required value={email} onChange={e => setEmail(e.target.value)} placeholder={t("auth.emailPlaceholder")} aria-invalid={error ? true : undefined} />
						</div>
						<div className="flex flex-col gap-2">
							<Label htmlFor="password">{t("auth.password")}</Label>
							<PasswordInput id="password" autoComplete="current-password" required value={password} onChange={e_0 => setPassword(e_0.target.value)} placeholder={t("auth.passwordPlaceholder")} aria-invalid={error ? true : undefined} showLabel={t("users.password.show")} hideLabel={t("users.password.hide")} />
						</div>
						{error ? <p role="alert" className="text-sm text-destructive">
								{error}
							</p> : null}
						<Button type="submit" disabled={submitting} className="mt-2">
							{submitting ? t("auth.signingIn") : t("auth.submit")}
						</Button>
					</form>
				</CardContent>
			</Card>
		</div>;
}